import React, { useState } from 'react';
import { Star, BadgeCheck, ChevronLeft, Share2, Play, Phone, MapPin, Globe, Pencil } from 'lucide-react';
import { FloatingEditHotspot, ShareButton } from '@mts/ui';
import FavoriteButton from './FavoriteButton';
import BasicPageHeader from './BasicPageHeader';

export function HeroActions({ phone, website, directionsQuery, phoneField, websiteField, directionsField }) {
  const hasPhone = Boolean(String(phone || '').trim());
  const hasWebsite = Boolean(String(website || '').trim());
  const hasDirections = Boolean(String(directionsQuery || '').trim());
  const directionsHref = hasDirections
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(directionsQuery)}`
    : '#';

  return (
    <div
      id="HeroCard-HeroActions"
      className="flex w-full flex-wrap gap-2 [&>a]:flex-1"
    >
      {hasPhone ? (
        <a
          id="HeroCard-HeroActions-Call"
          data-listing-action="phone"
          href={`tel:${phone}`}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-md border px-4 text-sm font-semibold"
          style={{ borderColor: 'var(--ui-input-border)', color: 'var(--text)' }}
          title={`listing field: ${phoneField}`}
        >
          <Phone size={15} aria-hidden="true" />
          Call
        </a>
      ) : null}

      {hasDirections ? (
        <a
          id="HeroCard-HeroActions-Directions"
          href={directionsHref}
          target="_blank"
          rel="noreferrer"
          className="inline-flex h-10 items-center justify-center gap-2 rounded-md border px-4 text-sm font-semibold"
          style={{ borderColor: 'var(--ui-input-border)', color: 'var(--text)' }}
          title={`listing field: ${directionsField}`}
        >
          <MapPin size={15} aria-hidden="true" />
          Directions
        </a>
      ) : null}

      {hasWebsite ? (
        <a
          id="HeroCard-HeroActions-Website"
          data-listing-action="website"
          href={website}
          target="_blank"
          rel="noreferrer"
          className="inline-flex h-10 items-center justify-center gap-2 rounded-md border px-4 text-sm font-semibold"
          style={{ borderColor: 'var(--ui-input-border)', color: 'var(--text)' }}
          title={`listing field: ${websiteField}`}
        >
          <Globe size={15} aria-hidden="true" />
          Website
        </a>
      ) : null}
    </div>
  );
}

function BasicHeroCard({
  shareData,
  name,
  category,
  area,
  rating,
  reviews,
  description,
  logoUrl,
  nameField,
  categoryField,
  areaField,
  ratingField,
  reviewsField,
  descriptionField,
  logoField,
  phone,
  website,
  directionsQuery,
  phoneField,
  websiteField,
  directionsField,
  navigationLocked = false,
  isFeaturedTier = false,
  isSpotlightTier = false,
  spotlightBackgroundImageUrl = '',
  listingId,
  previewHotspot = null,
  onPreviewHotspotClick,
  forceBusinessNameUntint = false,
  canEdit = false,
  onEditName,
}) {
  const initials = String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'LB';
  const metaLine = [category, area].filter(Boolean).join(' • ');
  const spotlightImageUrl = String(spotlightBackgroundImageUrl || logoUrl || '').trim();
  const [spotlightLogoElement, setSpotlightLogoElement] = useState(null);
  const [standardLogoElement, setStandardLogoElement] = useState(null);
  const [spotlightHeroElement, setSpotlightHeroElement] = useState(null);
  const [nameElement, setNameElement] = useState(null);
  const [businessNameElement, setBusinessNameElement] = useState(null);
  const [descriptionElement, setDescriptionElement] = useState(null);

  const hotspotPlacement = previewHotspot?.placement || 'top-end';
  const hotspotOffsetPx = previewHotspot?.offsetPx ?? -6;
  const hotspotLabel = previewHotspot?.label || 'Edit highlighted section';
  const businessNameStyle = forceBusinessNameUntint
    ? { opacity: 1, filter: 'none', color: 'var(--text, #111827)' }
    : undefined;
  const heroRootStyle = {
    border: '1px solid var(--border)',
  };

  const resolveHotspotReference = () => {
    switch (previewHotspot?.target) {
      case 'logo':
        return isSpotlightTier ? spotlightLogoElement : standardLogoElement;
      case 'heroImage':
        return spotlightHeroElement;
      case 'name':
        return nameElement;
      case 'businessName':
        return businessNameElement || nameElement;
      case 'description':
        return descriptionElement;
      default:
        return null;
    }
  };

  const hotspotReferenceElement = resolveHotspotReference();

  if (isSpotlightTier) {
    return (
      <div
        id="HeroCard"
        className="overflow-hidden rounded-xl border text-black"
        style={{ backgroundColor: 'var(--card)', ...heroRootStyle }}
      >
        <div id="HeroCard-Header">
          <BasicPageHeader
            shareData={shareData}
            listingId={listingId}
            isFeaturedTier={isFeaturedTier}
            isSpotlightTier={isSpotlightTier}
            navigationLocked={navigationLocked}
          />
        </div>

        <div
          id="HeroCard-HeroMedia"
          ref={setSpotlightHeroElement}
          className="relative h-52 sm:h-64"
          style={spotlightImageUrl
            ? {
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.42), rgba(0, 0, 0, 0.42)), url(${spotlightImageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }
            : { backgroundColor: 'var(--surface)' }}
        >
          <div
            id="HeroCard-TopControls"
            className="absolute left-3 right-3 top-3 z-20 flex items-center justify-between"
          >
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full"
              style={{ color: 'var(--spotlight)', backgroundColor: 'transparent' }}
              aria-label="Back"
            >
              <ChevronLeft size={18} />
            </button>

            <div className="flex items-center gap-2">
              <FavoriteButton
                id="HeroCard-FavoriteButton"
                listingId={listingId}
                size={16}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full p-0"
                style={{ color: 'var(--spotlight)', backgroundColor: 'transparent' }}
              />
              {shareData ? <ShareButton {...shareData} variant="ghost" size="xs"
                type="button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full p-0"
                style={{ color: 'var(--spotlight)', backgroundColor: 'transparent' }}
                aria-label="Share listing"
              >
                <Share2 size={16} />
              </ShareButton> : null}
            </div>
          </div>

          <div
            id="HeroCard-SpotlightBadge"
            className="absolute left-3 top-12 z-20 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold"
            style={{ backgroundColor: 'var(--spotlight)', color: 'var(--spotlight-foreground, #111)' }}
          >
            <BadgeCheck size={12} />
            SPOTLIGHT
          </div>

          <div id="HeroCard-PlayControl">
            <button
              type="button"
              className="absolute right-4 top-1/2 z-20 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border"
              style={{ borderColor: 'rgba(255,255,255,0.9)', color: '#fff', backgroundColor: 'rgba(0,0,0,0.28)' }}
              aria-label="Play"
            >
              <Play size={20} className="translate-x-[1px]" />
            </button>
          </div>
        </div>

        <div id="HeroCard-Content" className="relative bg-white px-4 pb-4 pt-12">
          <div
            id="HeroCard-Logo"
            ref={setSpotlightLogoElement}
            className="absolute -top-9 left-4 shrink-0"
            title={`listing field: ${logoField}`}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={`${name} logo`}
                className="h-20 w-20 rounded-xl border-2 object-cover"
                style={{ borderColor: 'var(--spotlight)', backgroundColor: 'var(--card)' }}
                loading="lazy"
              />
            ) : (
              <div
                className="flex h-20 w-20 items-center justify-center rounded-xl border-2 text-sm font-semibold"
                style={{ borderColor: 'var(--spotlight)', backgroundColor: 'var(--card)', color: 'var(--spotlight)' }}
              >
                {initials}
              </div>
            )}
          </div>
          {previewHotspot?.target ? (
            <FloatingEditHotspot
              referenceElement={hotspotReferenceElement}
              placement={hotspotPlacement}
              offsetPx={hotspotOffsetPx}
              size={24}
              iconSize={12}
              label={hotspotLabel}
              onClick={onPreviewHotspotClick}
            />
          ) : null}

          <div id="HeroCard-TextBlock">
            <div className="relative pr-11">
              <h1
                id="listingName"
                ref={(node) => {
                  setNameElement(node);
                  setBusinessNameElement(node);
                }}
                className="text-3xl font-bold leading-tight text-black"
                style={businessNameStyle}
                title={`listing field: ${nameField}`}
              >
                {name}
              </h1>
              {canEdit ? (
                <button
                  type="button"
                  aria-label="Edit business name"
                  title="Edit business name"
                  onClick={onEditName}
                  className="absolute right-0 top-0 inline-flex h-9 w-9 items-center justify-center rounded-md border border-[var(--border)] text-[var(--primary)] transition-colors hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/30"
                >
                  <Pencil size={16} aria-hidden="true" />
                </button>
              ) : null}
            </div>

            <p className="mt-1 text-lg text-black/80" title={`listing fields: ${categoryField}${area ? `, ${areaField}` : ''}`}>
              {metaLine || category}
            </p>

            {rating > 0 ? (
              <p className="mt-2 flex items-center gap-1.5 text-base text-black/85" title={`listing fields: ${ratingField}, ${reviewsField}`}>
                <Star size={15} className="fill-current text-black" />
                {rating.toFixed(1)}
                <span className="opacity-80">({reviews} reviews)</span>
              </p>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id="HeroCard"
      className="overflow-hidden rounded-none border-0 text-black relative"
      style={{ backgroundColor: 'var(--card)', ...heroRootStyle }}
    >
      <div id="HeroCard-Header">
        <BasicPageHeader
          shareData={shareData}
          listingId={listingId}
          isFeaturedTier={isFeaturedTier}
          isSpotlightTier={isSpotlightTier}
          navigationLocked={navigationLocked}
        />
      </div>

      {isFeaturedTier && (
        <div
          id="HeroCard-FeaturedBadge"
          className="absolute top-2 left-2 z-10 flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold shadow"
          style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
          title="Featured Listing"
        >
          <BadgeCheck size={14} className="inline-block mr-1" />
          FEATURED
        </div>
      )}
      <div id="HeroCard-Body" className="space-y-3 p-3 sm:p-4">
        <div
          id="HeroCard-MainContent"
          className="flex flex-col gap-4 lg:flex-row lg:items-start"
        >
          <div
            id="HeroCard-LeftColumn"
            className="flex justify-center lg:basis-1/4 lg:justify-center"
          >
            <div id="HeroCard-Logo" ref={setStandardLogoElement} className="shrink-0 p-2">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={`${name} logo`}
                  className="h-24 w-24 rounded-md border border-black/20 object-cover"
                  loading="lazy"
                  title={`listing field: ${logoField}`}
                />
              ) : (
                <div
                  className="flex h-24 w-24 items-center justify-center rounded-md border border-black/20 text-xs font-semibold tracking-[0.06em] text-black"
                  style={{ backgroundColor: 'var(--card)' }}
                  title={`listing field: ${logoField}`}
                >
                  {initials}
                </div>
              )}
            </div>
          </div>

          <div
            id="HeroCard-RightColumn"
            className="space-y-2 text-center lg:basis-3/4 lg:text-left"
          >
          {previewHotspot?.target ? (
            <FloatingEditHotspot
              referenceElement={hotspotReferenceElement}
              placement={hotspotPlacement}
              offsetPx={hotspotOffsetPx}
              size={26}
              iconSize={12}
              label={hotspotLabel}
              onClick={onPreviewHotspotClick}
            />
          ) : null}

            <div id="HeroCard-TextBlock">
            <div className="relative mx-auto max-w-[18rem] pr-11 lg:mx-0 lg:max-w-none">
              <h1
                id="listingName"
                ref={(node) => {
                  setNameElement(node);
                  setBusinessNameElement(node);
                }}
                className="break-words text-2xl font-bold leading-tight text-black"
                style={businessNameStyle}
                title={`listing field: ${nameField}`}
              >
                {name}
              </h1>
              {canEdit ? (
                <button
                  type="button"
                  aria-label="Edit business name"
                  title="Edit business name"
                  onClick={onEditName}
                  className="absolute right-0 top-0 inline-flex h-9 w-9 items-center justify-center rounded-md border border-[var(--border)] text-[var(--primary)] transition-colors hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/30"
                >
                  <Pencil size={16} aria-hidden="true" />
                </button>
              ) : null}
            </div>

            {metaLine ? (
              <p className="mt-2 text-sm text-black/70" title={`listing fields: ${categoryField}${area ? `, ${areaField}` : ''}`}>
                {metaLine}
              </p>
            ) : null}

            {rating > 0 ? (
              <p className="mt-1 flex items-center justify-start gap-1 text-sm text-black/80" title={`listing fields: ${ratingField}, ${reviewsField}`}>
                <Star size={14} className="fill-current text-black" />
                {rating.toFixed(1)}
                <span className="opacity-80">({reviews} reviews)</span>
              </p>
            ) : null}
            </div>

            {description ? (
              <div id="HeroCard-Description">
                <p ref={setDescriptionElement} className="text-sm leading-6 text-black/80" title={`listing field: ${descriptionField}`}>
                  {description}
                </p>
              </div>
            ) : null}
          </div>
        </div>

        <HeroActions
          phone={phone}
          website={website}
          directionsQuery={directionsQuery}
          phoneField={phoneField}
          websiteField={websiteField}
          directionsField={directionsField}
        />
      </div>
    </div>
  );
}

export default BasicHeroCard;
